import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { queryCache, useQuery } from 'react-query';

import {
  AutoComplete,
  AutoCompleteList,
  AutoCompleteOption,
} from 'shopper/components/AutoComplete';
import Button from 'shopper/components/Button';
import Flex from 'shopper/components/Flex';
import Icon from 'shopper/components/Icon';

import { stripDuplicates } from 'lib/array';
import { sendEvent } from 'lib/gtag';
import { toCleanedSearchResultsPage } from 'lib/links';
import { getFromLocalStorage, setLocalStorage } from 'lib/localStorage';
import { searchQueryKeys } from 'lib/queryKeys';
import { get } from 'lib/request';
import { debounce } from 'lib/utils';

import { SEARCH_RESULTS_TYPES } from 'constants/searchResults';
import TOPBAR from 'constants/topbar';
import Z_INDEX_SCALE from 'constants/zIndexScale';

const MAX_INITIAL_DATA_LENGTH = 4;
const AUTOCOMPLETE_INITIALIZATION_LENGTH = 2;
const MIN_SEARCH_VALUE_TO_SEND = 2;
const RECENT_SEARCHES_LIMIT = 4;

/**
 * This array of sponsored ads will be hardcoded
 * because we haven't got enough time to automate and make it better
 * e.g.: [{
 *  icon: <Icon name="ame" aria-hidden />,
 *  text: 'Cashback Ame',
 *  value: 'ame',
 *  sponsored: true,
 * }]
 */
const sponsoredAdsSearch = [];

const promobitRecentSearch = JSON.parse(
  getFromLocalStorage('promobit.recent-search')
);

const sponsoredAdsText = sponsoredAdsSearch.map(({ text }) => text);

const localRecentSearch = promobitRecentSearch
  ? stripDuplicates([...promobitRecentSearch.splice(-RECENT_SEARCHES_LIMIT)])
  : [];

const writeRecentSearchOnLocalStorage = async (value) => {
  if (localRecentSearch.length > 4 - sponsoredAdsSearch.length) {
    localRecentSearch.splice(-RECENT_SEARCHES_LIMIT);
  }

  if (localRecentSearch.length === 4 - sponsoredAdsSearch.length) {
    localRecentSearch.shift();
  }

  if (localRecentSearch.includes(value)) {
    return;
  }

  setLocalStorage(
    'promobit.recent-search',
    JSON.stringify([...localRecentSearch, value])
  );
};

const getRecentSearchAsAutoCompleteData = () => {
  if (localRecentSearch.length === 0) {
    return [...sponsoredAdsSearch];
  }

  const recentSearches = [...localRecentSearch, ...sponsoredAdsText];
  const limitedRecentSearches = recentSearches
    .slice(0, MAX_INITIAL_DATA_LENGTH)
    .reverse();

  return limitedRecentSearches.map((recentSearch) => {
    const isRecentSearchWordOnSponsoredAds = sponsoredAdsSearch.some(
      ({ text: sponsoredAdText }) => sponsoredAdText === recentSearch
    );

    if (isRecentSearchWordOnSponsoredAds) {
      return sponsoredAdsSearch.find(
        (sponsoredAd) => sponsoredAd.text === recentSearch
      );
    }

    return {
      icon: <Icon name="clock" />,
      text: recentSearch,
      value: recentSearch,
      sponsored: false,
    };
  });
};

const TopbarSearch = ({
  gaEventCategory,
  iconLeft,
  searchQuery,
  searchResultsType,
  withTrends,
  ...rest
}) => {
  const sendButtonRef = useRef(null);
  const [inputValue, setInputValue] = useState(
    searchQuery ? searchQuery.toLowerCase() : ''
  );
  const [isFocused, setFocused] = useState(false);
  const [searchTrends, setSearchTrends] = useState([]);

  useQuery(searchQueryKeys.trendsList(), {
    queryFn: () => get('/search/trends'),
    enabled: withTrends,
    staleTime: Infinity,
    onSuccess: (trendSuggestions) => {
      setSearchTrends(
        trendSuggestions
          ?.slice(0, MAX_INITIAL_DATA_LENGTH)
          .map((suggestion) => ({
            icon: <Icon name="search" aria-hidden />,
            text: suggestion,
            value: suggestion,
          }))
      );
    },
  });

  const { data: initialValue } = useQuery(
    searchQueryKeys.autoCompleteInitialValue(),
    { initialData: '' }
  );

  const { data: autocompleteData, refetch } = useQuery(
    searchQueryKeys.autoCompleteValue({ value: inputValue.trim() }),
    {
      queryFn: () => get(`/search/autocomplete?q=${inputValue.trim()}`),
      enabled: false,
      initialData: [],
      keepPreviousData: true,
      staleTime: Infinity,
    }
  );

  const resetQueryCache = () =>
    queryCache.setQueryData(
      searchQueryKeys.autoCompleteValue({ value: inputValue.trim() }),
      []
    );

  const debouncedRefetch = useCallback(
    debounce(() => {
      refetch();
    }, 300),
    []
  );

  const isSponsored = (currInputValue) =>
    getRecentSearchAsAutoCompleteData().filter(
      ({ sponsored, text }) => sponsored && text === currInputValue
    ).length > 0;

  useEffect(() => {
    if (searchQuery) {
      setInputValue(searchQuery);
    }
  }, [searchQuery]);

  const onBlur = useCallback(() => setFocused(false), []);

  const onChange = useCallback(({ target: { value } }) => {
    setInputValue(value);

    if (value.length === 0) {
      resetQueryCache();
      return;
    }

    if (value.length >= AUTOCOMPLETE_INITIALIZATION_LENGTH) {
      debouncedRefetch();
    }
  }, []);

  const onFocus = useCallback(() => {
    sendEvent({ action: 'search', category: gaEventCategory });
    setFocused(true);
  }, [gaEventCategory]);

  const onOptionClick = async ({ target: { value } }) => {
    if (inputValue.length > 0) {
      sendEvent({
        action: 'search',
        category: 'autocomplete_click',
        data: value,
      });

      return;
    }

    if (
      inputValue.length === 0 &&
      searchTrends?.find(({ text }) => text === value)
    ) {
      const valueIndex = searchTrends.findIndex(({ text }) => text === value);
      sendEvent({
        action: `suggestion_search_recents_${valueIndex}`,
        category: gaEventCategory,
      });

      return;
    }

    sendEvent({
      action: 'recents_search_click',
      category: gaEventCategory,
      data: value,
    });
  };

  const onResetClick = useCallback(() => {
    resetQueryCache();
    setInputValue('');
    sendEvent({ action: 'input_search_delete', category: gaEventCategory });
  }, [gaEventCategory]);

  const onSend = useCallback(
    async (value) => {
      if (value.length < MIN_SEARCH_VALUE_TO_SEND) {
        return;
      }

      const searchedRecently = getRecentSearchAsAutoCompleteData();

      if (isSponsored(value)) {
        const sponsoredAdsSearchQuery = searchedRecently.find(
          ({ sponsored }) => sponsored === true
        ).value;

        return toCleanedSearchResultsPage(
          searchResultsType.PARAM,
          sponsoredAdsSearchQuery
        );
      }

      const isSearchedRecently = searchedRecently.find(
        ({ text }) => text === value
      );

      if (!isSearchedRecently) {
        await writeRecentSearchOnLocalStorage(value);
      }

      resetQueryCache();
      queryCache.setQueryData(
        searchQueryKeys.autoCompleteInitialValue(),
        value
      );
      toCleanedSearchResultsPage(searchResultsType.PARAM, value);
    },
    [searchResultsType]
  );

  const recentSearchOptions = getRecentSearchAsAutoCompleteData().filter(
    ({ text }) =>
      searchTrends.every(
        ({ text: searchTrendText }) => searchTrendText !== text
      )
  );

  return (
    <Flex flex={1} {...rest}>
      <AutoComplete
        data-test-selector="searchbar"
        placeholder="O que você procura?"
        iconLeft={iconLeft}
        maxOptions={8}
        searchStartsAt={AUTOCOMPLETE_INITIALIZATION_LENGTH}
        sendButtonRef={sendButtonRef}
        initialValue={initialValue}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        onResetClick={onResetClick}
        onSend={onSend}
        full
        overlay
      >
        {inputValue.length === 0 && (
          <>
            <AutoCompleteList
              title="BUSCAS RECENTES"
              options={recentSearchOptions}
              render={({ text, icon, sponsored }) => (
                <AutoCompleteOption
                  iconLeft={icon}
                  isSponsored={sponsored}
                  onClick={onOptionClick}
                >
                  {text}
                </AutoCompleteOption>
              )}
            />
            <AutoCompleteList
              title="PRODUTOS MAIS BUSCADOS"
              options={searchTrends}
              render={({ text, icon, sponsored }) => (
                <AutoCompleteOption
                  iconLeft={icon}
                  isSponsored={sponsored}
                  onClick={onOptionClick}
                >
                  {text}
                </AutoCompleteOption>
              )}
            />
          </>
        )}
        {autocompleteData.length > 0 && inputValue.length !== 0 && (
          <AutoCompleteList
            options={autocompleteData}
            render={({ text }) => (
              <AutoCompleteOption
                iconLeft={<Icon name="search" aria-hidden />}
                onClick={onOptionClick}
              >
                {text}
              </AutoCompleteOption>
            )}
          />
        )}
      </AutoComplete>
      <Button
        ref={sendButtonRef}
        size="size2"
        data-test-selector="search-button"
        aria-label="Pesquisar no Promobit"
        zIndex={isFocused ? Z_INDEX_SCALE.SIZE_6 : null}
        icon={<Icon name="search" aria-hidden />}
        ml={1}
        onClick={() => onSend(inputValue)}
      />
    </Flex>
  );
};

TopbarSearch.defaultProps = {
  gaEventCategory: TOPBAR.GA_EVENT_CATEGORY,
  iconLeft: null,
  searchQuery: null,
  searchResultsType: SEARCH_RESULTS_TYPES.GENERAL,
  withTrends: true,
};

TopbarSearch.propTypes = {
  gaEventCategory: PropTypes.string,
  iconLeft: PropTypes.node,
  searchQuery: PropTypes.string,
  searchResultsType: PropTypes.shape({
    NAME: PropTypes.string.isRequired,
    QUERY: PropTypes.string.isRequired,
    TYPE: PropTypes.string,
    BUTTON_TEXT: PropTypes.string,
    GA_EVENT_ACTION_PREFIX: PropTypes.string,
    GENERAL_RESULTS_LIMIT: PropTypes.number.isRequired,
    SORT_OPTIONS: PropTypes.arrayOf(
      PropTypes.shape({
        isDefaultOption: PropTypes.bool.isRequired,
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      })
    ),
  }),
  withTrends: PropTypes.bool,
};

export default TopbarSearch;
